import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, ShieldCheck, Users, GraduationCap, Eye, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bundled demo creds — one click per role
const DEMO_CREDS = [
  { role: 'Admin',     icon: Shield,         color: 'bg-orange-500',  identifier: 'admin',                    password: 'admin123' },
  { role: 'Principal', icon: GraduationCap,  color: 'bg-purple-500',  identifier: 'punam.jaiswal@sanjaya.in', password: 'sanjaya123' },
  { role: 'Observer',  icon: Eye,            color: 'bg-blue-500',    identifier: 'demo.observer@sanjaya.in', password: 'demo123' },
  { role: 'Parent',    icon: Users,          color: 'bg-emerald-500', identifier: 'demo.parent@sanjaya.in',   password: 'demo123' },
];

export default function UnifiedLogin() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Forgot-password flow state
  const [forgotMode, setForgotMode] = useState(null);  // null | 'request' | 'verify'
  const [forgotIdent, setForgotIdent] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPwd, setForgotNewPwd] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  // If a valid session token already exists, jump straight to the right dashboard
  useEffect(() => {
    if (localStorage.getItem('admin_token')) { navigate('/admin/panel', { replace: true }); return; }
    if (localStorage.getItem('principal_token')) { navigate('/principal/dashboard', { replace: true }); return; }
    if (localStorage.getItem('observer_token')) { navigate('/observer/dashboard', { replace: true }); return; }
    if (localStorage.getItem('parent_token')) { navigate('/parent/dashboard', { replace: true }); return; }
  }, [navigate]);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!identifier.trim() || !password) {
      setError('Please enter both your username/email and password.');
      return;
    }
    setIsLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        identifier: identifier.trim(),
        password,
      });

      // Clear ALL other portal tokens to avoid cross-contamination
      ['admin_token', 'admin_username',
       'principal_token', 'principal_id', 'principal_email', 'principal_name',
       'observer_token', 'observer_id', 'observer_email', 'observer_name',
       'parent_token', 'parent_id', 'parent_email', 'parent_name',
      ].forEach(k => localStorage.removeItem(k));

      // Persist the new session
      Object.entries(data.storage || {}).forEach(([k, v]) => localStorage.setItem(k, v));

      navigate(data.redirect_url, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fillDemo = (cred) => {
    setIdentifier(cred.identifier);
    setPassword(cred.password);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 flex items-center justify-center px-4 py-8" data-testid="unified-login-root">
      <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* LEFT — login form */}
        <Card className="md:col-span-3 border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <ShieldCheck className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Sign in to Sanjaya</h1>
                <p className="text-sm text-gray-600">One login for Admin, Principal, Observer & Parent</p>
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-5" data-testid="unified-login-form">
              <div>
                <Label htmlFor="identifier" className="text-gray-700 font-medium">Username or email</Label>
                <Input
                  id="identifier"
                  data-testid="unified-identifier"
                  type="text"
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="admin   or   you@school.com"
                  className="mt-2 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
                <Input
                  id="password"
                  data-testid="unified-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="mt-2 h-11"
                  required
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="unified-login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>{error}</div>
                </div>
              )}

              <Button
                type="submit"
                data-testid="unified-submit"
                disabled={isLoading}
                className="w-full h-11 bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white text-base"
              >
                <LogIn className="w-4 h-4 mr-2" />
                {isLoading ? 'Signing in…' : 'Sign in'}
              </Button>

              <button
                type="button"
                onClick={() => { setForgotMode('request'); setForgotIdent(identifier); setForgotMsg(''); }}
                className="w-full text-center text-sm text-gray-500 hover:text-orange-600 mt-2"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </form>

            <p className="text-xs text-gray-500 mt-6 text-center">
              The system will automatically take you to the correct dashboard based on your account type.
            </p>
          </CardContent>
        </Card>

        {/* RIGHT — 1-click demo creds */}
        <Card className="md:col-span-2 border-0 shadow-2xl bg-gradient-to-br from-slate-800 to-slate-900 text-white">
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-1">Quick demo login</h2>
            <p className="text-xs text-slate-300 mb-4">Click any role to auto-fill credentials, then press Sign in.</p>
            <div className="space-y-2">
              {DEMO_CREDS.map((c) => {
                const Icon = c.icon;
                return (
                  <button
                    key={c.role}
                    type="button"
                    data-testid={`demo-fill-${c.role.toLowerCase()}`}
                    onClick={() => fillDemo(c)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 transition-colors text-left group"
                  >
                    <div className={`w-9 h-9 ${c.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{c.role}</div>
                      <div className="text-xs text-slate-400 truncate">{c.identifier}</div>
                    </div>
                    <span className="text-xs text-slate-500 group-hover:text-orange-300">Fill →</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t border-slate-700 text-xs text-slate-400 space-y-1">
              <div>• Admin uses a <strong className="text-slate-200">username</strong> (admin), not an email.</div>
              <div>• Demo Parent / Observer share the password <code className="bg-slate-700 px-1 rounded">demo123</code>.</div>
              <div>• Principal password is <code className="bg-slate-700 px-1 rounded">sanjaya123</code>.</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forgot password modal */}
      {forgotMode && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setForgotMode(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="forgot-modal">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Reset password</h3>
            {forgotMode === 'request' && (
              <>
                <p className="text-sm text-gray-600 mb-4">Enter your username or email — we'll email you a 6-digit code valid for 15 min.</p>
                <Input
                  type="text" placeholder="admin or you@school.com"
                  value={forgotIdent} onChange={e => setForgotIdent(e.target.value)}
                  data-testid="forgot-identifier" autoFocus
                />
                {forgotMsg && <p className="text-xs mt-2 text-emerald-700">{forgotMsg}</p>}
                <div className="flex justify-end gap-2 mt-5">
                  <Button variant="outline" onClick={() => setForgotMode(null)}>Cancel</Button>
                  <Button
                    onClick={async () => {
                      if (!forgotIdent.trim()) return;
                      setForgotBusy(true);
                      try {
                        await axios.post(`${API}/auth/forgot-password`, { identifier: forgotIdent.trim() });
                        setForgotMsg('✓ If the account exists, a code has been emailed. Check your inbox.');
                        setTimeout(() => setForgotMode('verify'), 800);
                      } catch (e) {
                        setForgotMsg('Could not send code. Please try again.');
                      } finally { setForgotBusy(false); }
                    }}
                    disabled={forgotBusy}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="forgot-send-code"
                  >{forgotBusy ? 'Sending…' : 'Email me a code'}</Button>
                </div>
              </>
            )}
            {forgotMode === 'verify' && (
              <>
                <p className="text-sm text-gray-600 mb-4">Enter the 6-digit code from your inbox and choose a new password.</p>
                <Label className="text-xs">Username/email</Label>
                <Input type="text" value={forgotIdent} onChange={e => setForgotIdent(e.target.value)} className="mb-3" data-testid="verify-identifier" />
                <Label className="text-xs">6-digit code</Label>
                <Input
                  type="text" inputMode="numeric" maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={forgotCode} onChange={e => setForgotCode(e.target.value.replace(/\D/g, ''))}
                  className="mb-3 tracking-[6px] font-mono text-center text-lg"
                  data-testid="verify-code"
                />
                <Label className="text-xs">New password (min 6 chars)</Label>
                <Input type="password" value={forgotNewPwd} onChange={e => setForgotNewPwd(e.target.value)} placeholder="Enter new password" data-testid="verify-newpwd" />
                {forgotMsg && <p className={`text-xs mt-2 ${forgotMsg.startsWith('✓') ? 'text-emerald-700' : 'text-red-600'}`}>{forgotMsg}</p>}
                <div className="flex justify-end gap-2 mt-5">
                  <Button variant="outline" onClick={() => setForgotMode('request')}>Back</Button>
                  <Button
                    onClick={async () => {
                      if (forgotCode.length !== 6 || forgotNewPwd.length < 6) {
                        setForgotMsg('Code must be 6 digits and password ≥ 6 chars.');
                        return;
                      }
                      setForgotBusy(true);
                      try {
                        await axios.post(`${API}/auth/reset-password`, {
                          identifier: forgotIdent.trim(),
                          code: forgotCode,
                          new_password: forgotNewPwd,
                        });
                        setForgotMsg('✓ Password updated! Sign in with your new password.');
                        setTimeout(() => {
                          setForgotMode(null);
                          setPassword(forgotNewPwd);
                          setIdentifier(forgotIdent.trim());
                          setForgotCode(''); setForgotNewPwd('');
                        }, 1200);
                      } catch (e) {
                        setForgotMsg(e?.response?.data?.detail || 'Reset failed. Try again.');
                      } finally { setForgotBusy(false); }
                    }}
                    disabled={forgotBusy}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="verify-submit"
                  >{forgotBusy ? 'Resetting…' : 'Reset password'}</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
